<template>
  <div class="yee-bank-select-wrapper">
    <!-- Label -->
    <label v-if="label" class="select-label" @click="openPicker">
      <span v-if="required" class="required-star">*</span>
      {{ label }}
    </label>

    <!-- 触发器 -->
    <div
      class="select-trigger"
      :class="{ 'is-error': error, 'has-value': !!displayValue }"
      @click="openPicker"
    >
      <span class="trigger-text">{{ displayValue || placeholder }}</span>
      <span class="arrow-icon">‹</span>
    </div>

    <!-- 弹窗 -->
    <transition name="fade" @after-enter="onAfterEnter">
      <div v-if="showPicker" class="picker-mask" @click.self="closePicker">
        <div class="picker-container">
          <!-- 搜索栏（默认不聚焦）-->
          <div class="search-bar">
            <div class="search-input-wrapper" @click="focusSearch">
              <span class="search-icon">🔍</span>
              <input
                ref="searchInput"
                v-model="searchKeyword"
                type="text"
                placeholder="搜索银行名称"
                class="search-input"
                @input="onSearchInput"
              />
            </div>
          </div>

          <!-- 银行列表 -->
          <div class="bank-list">
            <RecycleScroller
              v-if="filteredList.length > 0"
              ref="scroller"
              class="bank-scroller"
              :items="filteredList"
              :item-size="52"
              key-field="bankType"
              v-slot="{ item }"
            >
              <div
                class="bank-item"
                :class="{ active: item.bankType === value }"
                @click="onSelect(item)"
              >
                <span class="bank-name">{{ item.bankName }}</span>
                <span v-if="item.bankType === value" class="check-icon">✓</span>
              </div>
            </RecycleScroller>

            <div v-else class="empty-state">
              <p>暂无匹配结果</p>
              <p class="hint">请尝试输入“工”、“建”、“招”等关键字</p>
            </div>
          </div>

          <!-- 底部取消按钮 -->
          <div class="picker-footer">
            <button class="cancel-btn" @click="closePicker">取消</button>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import { RecycleScroller } from 'vue-virtual-scroller'
import 'vue-virtual-scroller/dist/vue-virtual-scroller.css'

// ✅ 常用银行前置（前7位为高频银行）
const DEFAULT_BANK_LIST = [
  { bankType: 'ICBC', bankName: '中国工商银行' },
  { bankType: 'CCB', bankName: '中国建设银行' },
  { bankType: 'ABC', bankName: '中国农业银行' },
  { bankType: 'BOC', bankName: '中国银行' },
  { bankType: 'BOCOM', bankName: '交通银行' },
  { bankType: 'CMB', bankName: '招商银行' },
  { bankType: 'PSBC', bankName: '中国邮政储蓄银行' },
  // 其他银行（保持原有顺序）
  { bankType: 'CITIC', bankName: '中信银行' },
  { bankType: 'CMBC', bankName: '中国民生银行' },
  { bankType: 'CIB', bankName: '兴业银行' },
  { bankType: 'SPDB', bankName: '上海浦东发展银行' },
  { bankType: 'GDB', bankName: '广发银行' },
  { bankType: 'HXB', bankName: '华夏银行' },
  { bankType: 'PAB', bankName: '平安银行' },
  { bankType: 'CEB', bankName: '中国光大银行' }
]

export default {
  name: 'YeeBankSelect',
  components: { RecycleScroller },
  props: {
    value: { type: String, default: '' },
    label: { type: String, default: '' },
    required: { type: Boolean, default: false },
    bankList: { type: Array, default: () => [] },
    placeholder: { type: String, default: '请选择开户银行' },
    error: { type: Boolean, default: false }
  },
  data () {
    return {
      showPicker: false,
      searchKeyword: ''
      // 不再维护 recent 状态 —— 简洁优先，避免复杂逻辑
    }
  },
  computed: {
    safeBankList () {
      const list = Array.isArray(this.bankList) && this.bankList.length > 0
        ? this.bankList
        : DEFAULT_BANK_LIST

      // ✅ 强制常用银行置顶：工、建、农、中、交、招、邮储
      const PRIORITY_TYPES = ['ICBC', 'CCB', 'ABC', 'BOC', 'BOCOM', 'CMB', 'PSBC']
      const priorityOrder = new Map(PRIORITY_TYPES.map((type, idx) => [type, idx]))

      return [...list].sort((a, b) => {
        const orderA = priorityOrder.has(a.bankType) ? priorityOrder.get(a.bankType) : Infinity
        const orderB = priorityOrder.has(b.bankType) ? priorityOrder.get(b.bankType) : Infinity
        return orderA - orderB
      })
    },
    displayValue () {
      const found = this.safeBankList.find(b => b.bankType === this.value)
      return found ? found.bankName : ''
    },
    filteredList () {
      const list = this.safeBankList
      if (!list || list.length === 0) return []

      const kw = this.searchKeyword.trim().toLowerCase()
      if (kw) {
        return list.filter(item =>
          (item.bankName || '').toLowerCase().includes(kw)
        )
      }
      return list // 无搜索时直接返回已排序列表
    }
  },
  methods: {
    openPicker () {
      this.showPicker = true
    },
    closePicker () {
      this.showPicker = false
      // 清空搜索（关闭时重置，避免下次打开残留关键词）
      this.searchKeyword = ''
    },
    onAfterEnter () {
      this.$nextTick(() => {
        if (this.$refs.scroller) this.$refs.scroller.$forceUpdate()
      })
    },
    focusSearch () {
      // ✅ 用户主动点击搜索区域才聚焦，避免自动唤起键盘
      this.$nextTick(() => {
        if (this.$refs.searchInput) this.$refs.searchInput.focus()
      })
    },
    onSearchInput () {
      // 输入时刷新虚拟滚动（防白屏）
      this.$nextTick(() => {
        if (this.$refs.scroller) this.$refs.scroller.$forceUpdate()
      })
    },
    onSelect (item) {
      this.$emit('input', item.bankType)
      this.$emit('change', item)
      this.closePicker()
    }
  }
}
</script>

<style scoped>
.yee-bank-select-wrapper {
  width: 100%;
  font-family: -apple-system, BlinkMacSystemFont, "Helvetica Neue", sans-serif;
}

/* Label */
.select-label {
  display: block;
  font-size: 15px;
  color: #333;
  margin-bottom: 8px;
}

.required-star {
  color: #e53935;
  margin-right: 2px;
}

/* 触发器 */
.select-trigger {
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 52px;
  padding: 0 16px;
  background: #fff;
  border: 1px solid #e5e5e5;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
}

.select-trigger.has-value .trigger-text {
  color: #333;
  font-weight: 500;
}

.select-trigger.is-error {
  border-color: #e53935;
  box-shadow: 0 0 0 2px rgba(229, 57, 53, 0.2);
}

.trigger-text {
  font-size: 16px;
  color: #999;
  line-height: 1.5;
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.arrow-icon {
  font-size: 18px;
  color: #ccc;
  transform: rotate(-90deg);
  margin-left: 8px;
}

/* 弹窗遮罩 */
.picker-mask {
  position: fixed;
  top: 0; left: 0; right: 0; bottom: 0;
  background: rgba(0, 0, 0, 0.4);
  z-index: 10000;
  display: flex;
  align-items: flex-end;
}

.picker-container {
  width: 100%;
  height: 80vh;
  max-height: 80vh;
  background: #fff;
  border-radius: 16px 16px 0 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

/* 搜索栏 —— 默认不聚焦 */
.search-bar {
  padding: 14px 16px;
  background: #f8f8f8;
  border-bottom: 1px solid #eee;
}

.search-input-wrapper {
  position: relative;
  height: 38px;
  background: #fff;
  border-radius: 20px;
  overflow: hidden;
  cursor: text;
}

.search-icon {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 26px;
  color: #aaa;
}

.search-input {
  width: 100%;
  height: 100%;
  padding: 0 12px 0 36px;
  border: none;
  background: transparent;
  font-size: 26px;
  outline: none;
  box-sizing: border-box;
}

/* 银行列表 */
.bank-list {
  flex: 1;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  padding-left: 30px;
}

.bank-scroller {
  height: calc(100% - 56px); /* 减去 footer 高度 */
  width: 100%;
}

.bank-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 52px;
  padding: 0 16px;
  background: #fff;
  border-bottom: 1px solid #f5f5f5;
  cursor: pointer;
  transition: background 0.2s;
}

.bank-item:last-child {
  border-bottom: none;
}

.bank-item:active {
  background: #f9f9f9;
}

.bank-item.active {
  background: #f0f7ff;
  color: #4a90d9;
}

.bank-name {
  font-size: 26px;
  color: #333;
  line-height: 1.4;
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
}

.check-icon {
  font-size: 20px;
  color: #4a90d9;
  font-weight: bold;
  flex-shrink: 0;
  margin-left: 8px;
}

/* 空状态 */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: calc(100% - 56px);
  padding: 32px 20px;
  text-align: center;
  color: #999;
}

.empty-state p {
  font-size: 16px;
  line-height: 1.5;
}

.hint {
  font-size: 14px;
  color: #aaa;
  margin-top: 8px;
}

/* 底部取消 */
.picker-footer {
  padding: 16px;
  background: #f8f8f8;
  border-top: 1px solid #eee;
}

.cancel-btn {
  width: 100%;
  height: 65px;
  border: none;
  border-radius: 30px;
  background: #f0f0f0;
  color: #000000;
  font-size: 32px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.2s;
}

/* 动画 */
.fade-enter-active, .fade-leave-active {
  transition: opacity 0.3s ease;
}
.fade-enter, .fade-leave-to {
  opacity: 0;
}
</style>
